//
// File: Location.ts
// Description: Type definition for a Location - a location of a company store
// Copyright: Broadlume 2024
//

//The main properties of a location as from Nexus
//https://nexus.broadlu.me/swagger#/Retailer%20Stores

/**
 * The days of the week, used in the hours of operation
 */
export const LocationDayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

/**
 * The types of locations
 */
export const LocationTypes = {
  SHOWROOM: {
    name: 'Showroom',
    description:
      'A showroom is a physical location where customers can view and purchase products.',
    address: true,
  },
  'WAREHOUSE ONLY': {
    name: 'Warehouse Only',
    description: 'A warehouse is a physical location where products are stored and shipped from.',
    address: true,
  },
  'OFFICE ONLY': {
    name: 'Office Only',
    description: 'An office is a physical location where employees work.',
    address: true,
  },
  'MOBILE SHOWROOM': {
    name: 'Mobile Showroom',
    description: 'A mobile showroom is a vehicle that is used to display products to customers.',
    address: false,
  },
  OTHER: { name: 'Other', description: 'Some other type of location not listed', address: true },
} as const;

/**
 * Location opening hours for a particular day
 */
export type LocationDay = {
  day: (typeof LocationDayNames)[number];
  closed: boolean;
  opens_at: {
    hour: string | number;
    minute: string | number;
    second?: string | number;
    nano?: string | number;
  };
  closes_at: {
    hour: string | number;
    minute: string | number;
    second?: string | number;
    nano?: string | number;
  };
  by_appointment_only: boolean;
};

/**
 * The location type, representing a location of a company store
 */
export interface Location {
  /**
   * The unique identifier for the location
   * Optional when creating a new location as it will be generated by the server
   */
  id?: string;
  /**
   * The name of the location
   */
  name: string;
  /**
   * Address of the location
   */
  address?: {
    state: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
    city: string;
    street?: string;
    country_code: string;
    street_number: string;
    street_name: string;
    postal_code: string;
    secondary_address?: string;
    administrative_area?: string;
    locality?: string;
  };
  /**
   * The services offered at the location
   */
  services?: string[];
  /**
   * Id of the company that owns the location
   */
  retailer_id: string; //company id
  /**
   * Additional location name from Nexus schema
   */
  location_name?: string;
  /**
   * Location phone number from Nexus schema
   */
  phone_number?: string;
  /*
   * Locations main email address
   */
  time_zone?: string;
  /**
   * The hours of operation for the location
   */
  hours_of_operation?: LocationDay[];
  /**
   * The web address of the location
   */
  web_address?: string;
  /**
   * The google place id of the location
   */
  google_place_id?: string; //Google places ID
  /**
   * The type of location
   */
  location_type?: (typeof LocationTypes)[keyof typeof LocationTypes];
  /**
   * The status of the location
   */
  deactivated?: boolean;
}

// V2 API Types

/**
 * Address structure for V2 Location API
 */
export interface LocationV2Address {
  country?: string;
  administrative_area?: string;
  sub_administrative_area?: string;
  locality?: string;
  postal_code?: string;
  street?: string;
  premise?: string;
  longitude?: string;
  latitude?: string;
}

/**
 * Attributes for V2 Location API
 */
export interface LocationV2Attributes {
  web2textPhoneNumber?: string;
  [key: string]: string | undefined;
}

export type StoreHoursDayIndex = '0' | '1' | '2' | '3' | '4' | '5' | '6';
export type StoreHoursStatus = 'open' | 'closed' | 'appointmentOnly';

export interface StoreHoursTimeRange {
  from: string;
  to: string;
}

export interface LocationStoreHoursDay {
  status: StoreHoursStatus;
  timeRanges?: StoreHoursTimeRange[];
}

export interface LocationSpecialHoursEntry {
  id?: string;
  date: string;
  name: string;
  hours: LocationStoreHoursDay;
}

export interface LocationStoreHours {
  businessHours: Record<StoreHoursDayIndex, LocationStoreHoursDay>;
  specialHours: LocationSpecialHoursEntry[];
}

/**
 * Request body for creating a location via V2 API
 */
export interface CreateLocationV2Request {
  name: string;
  address?: LocationV2Address;
  phoneNumber?: string;
  altPhoneNumber?: string;
  altPhoneNumberLabel?: string;
  attributes?: LocationV2Attributes;
  storeType?: string;
  storeName?: string;
  callTrackingNumbers?: string[];
  retailerAccountName?: string;
  displayName?: string;
  storeEmail?: string;
  storeFaxNumber?: string;
  timezone?: string;
  offerings?: string[];
  storeFrontImageUrl?: string;
  gbpUrl?: string;
  gbpTrackingPhoneNumber?: string;
  gbpMapEmbed?: string;
  universalId?: string;
  universalLocationId?: string;
  status?: 'open' | 'closed';
  showOnWebsiteForms?: boolean;
  verificationStatus?: boolean;
  storeHours?: LocationStoreHours | null;
  yextCategoryKey?: string;
  yextEntityId?: string;
  yextDescription?: string;
  landingPage?: {
    publishLandingPage?: boolean;
    additionalContent?: string;
    useCodeEditor?: boolean;
  };
}

/**
 * Response type for V2 Location API
 */
export interface LocationV2 {
  id: string;
  name: string;
  address?: LocationV2Address;
  phoneNumber?: string;
  altPhoneNumber?: string;
  altPhoneNumberLabel?: string;
  attributes?: LocationV2Attributes;
  storeType?: string;
  storeName?: string;
  callTrackingNumbers?: string[];
  retailerAccountName?: string;
  displayName?: string;
  storeEmail?: string;
  storeFaxNumber?: string;
  timezone?: string;
  offerings?: string[];
  storeFrontImageUrl?: string;
  gbpUrl?: string;
  gbpTrackingPhoneNumber?: string;
  gbpMapEmbed?: string;
  universalId?: string;
  universalLocationId?: string;
  status: 'active' | 'inactive';
  createdAt?: string;
  updatedAt?: string;
  showOnWebsiteForms?: boolean;
  landingPage?: {
    publishLandingPage?: boolean;
    additionalContent?: string;
    slug?: string;
    useCodeEditor?: boolean;
  };
  verificationStatus?: boolean;
  verifiedAt?: string;
  storeHours?: LocationStoreHours | null;
  yextCategoryKey?: string;
  yextEntityId?: string;
  yextDescription?: string;
}

/**
 * Yext Category type for location categorization
 */
export interface YextCategory {
  key: string;
  label: string;
  fullName: string;
  isDefault?: boolean;
}

export interface TimezoneDtoType {
  name: string;
  abbrev: string;
  utc_offset: string;
}

/**
 * Yext Category type for location categorization
 */
export interface YextCategory {
  key: string;
  label: string;
  fullName: string;
  isDefault?: boolean;
}

export interface TimezoneDtoType {
  name: string;
  abbrev: string;
  utc_offset: string;
}
